import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Mic, Paperclip, Loader2, History, Plus, ChevronDown, Clock, Search, X, Hash, Cpu, Layout, Microscope, DollarSign } from 'lucide-react';
import { useChat } from '@ai-sdk/react';
import { useAuth } from '../../contexts/AuthContext';
import { API_CHAT_STREAM_URL, API_BASE_URL } from '../../config';
import AIResponse from './AIResponse';
import { useUsageQuota } from '../../hooks/useUsageQuota';
import axios from 'axios';

const AIChatSidebar = ({
    mode = 'chat',
    initialMessage = null,
    onActionTrigger = null, // Function to trigger parent actions (like takeScreenshot)
    onApplyFilters = null,  // Function to apply filters to parent state (screener)
    placeholder = "Ask anything...",
    className = "",
    loadHistory = false,
    initialThreadId = null,
    onOpenPdf = null,
}) => {
    const { isAuthenticated } = useAuth();
    const { refetch: refetchUsage } = useUsageQuota();
    const [localInput, setLocalInput] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [isSessionsLoading, setIsSessionsLoading] = useState(false);
    const [showSessionsDropdown, setShowSessionsDropdown] = useState(false);

    // Symbols State
    const [selectedSymbols, setSelectedSymbols] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSymbolsActive, setIsSymbolsActive] = useState(false);
    const [suggestionQuery, setSuggestionQuery] = useState('');
    const [caretPosition, setCaretPosition] = useState({ top: 0, left: 0 });
    const [isAtBottom, setIsAtBottom] = useState(true);
    const chatAreaRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const threadIdRef = useRef(initialThreadId || crypto.randomUUID());
    // Memoize the chat configuration to prevent re-initialization on every render
    const chatConfig = React.useMemo(() => ({
        api: API_CHAT_STREAM_URL,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        initialMessages: initialMessage ? [initialMessage] : [],
    }), [initialMessage, refetchUsage]);

    const chat = useChat(chatConfig);

    const {
        messages,
        append,
        sendMessage,
        status,
        setMessages,
        isLoading: sdkLoading
    } = chat;


    // Refetch usage counter after each completed response
    const prevStatusRef = useRef(status);
    useEffect(() => {
        if (prevStatusRef.current !== 'ready' && status === 'ready' && messages.length > 0) {
            refetchUsage();
        }
        if (status === 'error' && chat.error) {
            console.error('AIChatSidebar: chat error:', chat.error);
        }
        prevStatusRef.current = status;
    }, [status, chat.error, messages.length, refetchUsage]);

    // Load sessions
    const fetchSessions = async () => {
        setIsSessionsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/agent/sessions/`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setSessions(response.data);
        } catch (error) {
            console.error('Failed to load sessions:', error);
        } finally {
            setIsSessionsLoading(false);
        }
    };
    const cleanContent = (text) => {
        if (!text) return text;
        // Strip tag markers and encoded tags
        return text
            .replace(/<[^>]*>/g, '')
            .replace(/&lt;.*?&gt;/g, '')
            .trim();
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchSessions();
        }
    }, [isAuthenticated]);

    // Handle initial thread load or thread changes from props
    useEffect(() => {
        if (isAuthenticated && initialThreadId) {
            handleSelectSession(initialThreadId);
        }
    }, [isAuthenticated, initialThreadId]);

    // Ref to injectAnalysisRequest — updated every render so the timer callback always
    // gets the latest version (injectAnalysisRequest is defined further down in the file).
    const injectRef = useRef(null);

    // Auto-send: read once from sessionStorage on mount (set by PredictionDetailPage).
    // useEffect runs after render so injectRef.current is already populated.
    useEffect(() => {
        const pendingMsg = sessionStorage.getItem('chatAutoSend');
        if (!pendingMsg) return;
        sessionStorage.removeItem('chatAutoSend');
        const timer = setTimeout(() => {
            if (injectRef.current) injectRef.current(pendingMsg, null);
        }, 500);
        return () => clearTimeout(timer);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelectSession = async (sessionId) => {
        setIsHistoryLoading(true);
        setShowSessionsDropdown(false);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/agent/sessions/${sessionId}/`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            threadIdRef.current = response.data.session.id;

            const historyMessages = response.data.messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                thought: m.thought,
                createdAt: new Date(m.created_at)
            }));

            setMessages(historyMessages);
        } catch (error) {
            console.error('Failed to load session history:', error);
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const handleNewChat = () => {
        threadIdRef.current = crypto.randomUUID();
        setMessages([]);
        setShowSessionsDropdown(false);
        setSelectedSymbols([]);
        setSuggestionQuery('');
        setShowSuggestions(false);
    };

    // Debounced Symbol Fetching
    const fetchSuggestions = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsSuggestionsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/symbols/?symbol=${query}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setSuggestions(response.data);
            setShowSuggestions(response.data.length > 0);
        } catch (error) {
            console.error('Failed to fetch symbol suggestions:', error);
            setSuggestions([]);
        } finally {
            setIsSuggestionsLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (suggestionQuery && (isSymbolsActive || suggestionQuery.startsWith('$'))) {
                const query = suggestionQuery.startsWith('$') ? suggestionQuery.slice(1) : suggestionQuery;
                // Trigger for any character if it's a $ trigger, or 1+ char if symbols mode active
                if (query.length >= 0) {
                    fetchSuggestions(query);
                } else {
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [suggestionQuery, fetchSuggestions, isSymbolsActive]);

    const handleSelectSymbol = (symbol) => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            // Find the word that triggered the suggestion
            const textNode = range.startContainer;
            const text = textNode.textContent;
            const offset = range.startOffset;

            // Find start of the word
            let start = offset - 1;
            while (start >= 0 && !/\s/.test(text[start])) {
                start--;
            }
            start++;

            // Create range for the word to replace
            const replaceRange = document.createRange();
            replaceRange.setStart(textNode, start);
            replaceRange.setEnd(textNode, offset);
            replaceRange.deleteContents();

            // Create tag
            const tag = document.createElement('span');
            tag.className = 'inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-600 text-white rounded-md text-xs font-semibold mx-0.5 align-baseline';
            tag.dataset.symbol = symbol.symbol;
            tag.dataset.exchange = symbol.full_symbol?.includes(':') ? symbol.full_symbol.split(':')[0] : '';
            tag.contentEditable = 'false';
            tag.textContent = symbol.symbol;

            // Create a wrapper for better behavior
            const fragment = document.createDocumentFragment();
            fragment.appendChild(tag);
            fragment.appendChild(document.createTextNode('\u00A0')); // Non-breaking space

            replaceRange.insertNode(fragment);

            // Move caret after the space
            const newRange = document.createRange();
            newRange.setStartAfter(tag.nextSibling);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }

        setSuggestionQuery('');
        setShowSuggestions(false);
        // Important: sync localInput if needed, though we'll read direct from DOM on send
    };

    const getCaretCoordinates = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const containerRect = inputRef.current.getBoundingClientRect();

            return {
                top: rect.top - containerRect.top,
                left: rect.left - containerRect.left
            };
        }
        return { top: 0, left: 0 };
    };

    const removeSymbol = (symbolToRemove) => {
        // This function is now legacy as tags are managed within the contenteditable div
    };


    const isLoading = status === 'streaming' || status === 'submitting' || sdkLoading;

    const handleScroll = () => {
        if (chatAreaRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
            // Use a small buffer (50px) to consider it "at bottom" for auto-scrolling
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(isNearBottom);
        }
    };

    // Scroll to bottom on updates
    useEffect(() => {
        if (chatAreaRef.current) {
            const lastMessage = messages[messages.length - 1];
            const isUserMessage = lastMessage?.role === 'user';

            if (isAtBottom || isUserMessage) {
                chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
            }
        }
    }, [messages, status]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeSelectedFile = () => {
        setSelectedFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (const item of items) {
            if (item.type.indexOf("image") !== -1) {
                const file = item.getAsFile();
                if (file) {
                    // Prevent default behavior of inserting image into the div
                    e.preventDefault();

                    setSelectedFile(file);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                }
            }
        }
    };

    const getMessageData = (msg) => {
        if (!msg) return [];
        const partsData = msg.parts?.map(p => {
            if (p.type?.startsWith('data-') || p.type === 'thought-delta') {
                const item = p.data || p;
                if (typeof item === 'object' && item !== null) {
                    return { ...item, sseType: p.type };
                }
                return { value: item, sseType: p.type };
            }
            return null;
        }).filter(Boolean) || [];
        const legacyData = Array.isArray(msg.data) ? msg.data : (msg.data ? [msg.data] : []);
        const annotations = msg.annotations || [];
        return [...partsData, ...legacyData, ...annotations];
    };

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();

        const inputContainer = inputRef.current;
        if (!inputContainer) return;

        // Process content to extract text and replace tags with symbols
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = inputContainer.innerHTML;

        // Find all tags and get their symbols
        const tags = tempDiv.querySelectorAll('[data-symbol]');
        const symbols = Array.from(tags).map(t => t.dataset.symbol);

        // Replace tag elements with symbol and exchange text
        tags.forEach(tag => {
            const replacement = tag.dataset.exchange
                ? `${tag.dataset.symbol}:${tag.dataset.exchange}`
                : tag.dataset.symbol;
            tag.replaceWith(replacement);
        });

        const currentInput = tempDiv.innerText.trim();

        if ((currentInput || selectedFile) && !isLoading) {
            const chatData = {
                images: imagePreview ? [imagePreview] : [],
                symbols: symbols, // Extracted symbols from tags
                thread_id: threadIdRef.current,
                mode: mode
            };


            const chatOptions = {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                data: chatData,
                body: chatData
            };

            const message = {
                role: 'user',
                content: currentInput,
                experimental_attachments: imagePreview ? [
                    {
                        url: imagePreview,
                        name: 'upload.png',
                        contentType: 'image/png'
                    }
                ] : []
            };

            inputContainer.innerHTML = '';
            setLocalInput('');
            setSelectedSymbols([]);
            removeSelectedFile();

            if (chat.append) {
                await chat.append(message, chatOptions);
            } else if (append) {
                await append(message, chatOptions);
            } else if (sendMessage) {
                await sendMessage(message, chatOptions);
            }
        }
    };

    const injectAnalysisRequest = async (prompt, imageData) => {
        if (isLoading) return;

        // Extract symbols from input if possible, or just send empty for analysis
        const inputContainer = inputRef.current;
        let symbols = [];
        if (inputContainer) {
            const tags = inputContainer.querySelectorAll('[data-symbol]');
            symbols = Array.from(tags).map(t => t.dataset.symbol);
        }

        const chatOptions = {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            data: {
                images: imageData ? [imageData] : [],
                symbols: symbols,
                thread_id: threadIdRef.current,
                mode: mode
            }
        };

        const message = {
            role: 'user',
            content: prompt,
            experimental_attachments: imageData ? [
                {
                    url: imageData,
                    name: 'chart.png',
                    contentType: 'image/png'
                }
            ] : []
        };

        if (chat.append) {
            await chat.append(message, chatOptions);
        } else if (append) {
            await append(message, chatOptions);
        } else if (sendMessage) {
            await sendMessage(message, chatOptions);
        }
    };

    // Keep ref up-to-date so the sessionStorage auto-send timer always calls the latest version
    injectRef.current = injectAnalysisRequest;

    const handleProposalEdit = (text) => {
        if (inputRef.current) {
            inputRef.current.innerText = text;
            inputRef.current.focus();
            // Move cursor to end
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };

    const handleDecision = useCallback(async (decision) => {
        if (isLoading) return;

        const chatData = {
            thread_id: threadIdRef.current,
            mode: mode
        };

        const chatOptions = {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            data: chatData,
            body: chatData
        };

        const resumePayload = {
            type: 'resume',
            decision: decision
        };

        const message = {
            role: 'user',
            content: JSON.stringify(resumePayload)
        };

        if (chat.append) {
            await chat.append(message, chatOptions);
        } else if (sendMessage) {
            await sendMessage(message, chatOptions);
        }
    }, [isLoading, mode, chat, sendMessage]);

    const latestMessage = messages[messages.length - 1];
    const streamData = latestMessage ? getMessageData(latestMessage) : [];

    return (
        <div className={`flex flex-col h-full bg-white border-l border-gray-200 ${className}`}>
            <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13 3L4 14H11L11 21L20 10H13L13 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        {mode === 'chart' ? 'AI Chart Copilot' : mode === 'screener' ? 'AI Screening Copilot' : 'AnalystBro AI'}
                    </h3>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={handleNewChat}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="New Chat"
                        >
                            <Plus size={18} />
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => setShowSessionsDropdown(!showSessionsDropdown)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-0.5"
                                title="Chat History"
                            >
                                <History size={18} />
                                <ChevronDown size={14} />
                            </button>

                            {showSessionsDropdown && (
                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-[400px] overflow-hidden flex flex-col">
                                    <div className="p-3 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-tight">Recent Chats</span>
                                        <button onClick={fetchSessions} className="text-[10px] text-blue-600 font-bold hover:underline">Refresh</button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                                        {isSessionsLoading ? (
                                            <div className="p-4 flex justify-center"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
                                        ) : sessions.length === 0 ? (
                                            <div className="p-8 text-center text-gray-400 text-xs">No saved chats yet</div>
                                        ) : (
                                            sessions.map(session => (
                                                <button
                                                    key={session.id}
                                                    onClick={() => handleSelectSession(session.id)}
                                                    className={`w-full text-left p-3 hover:bg-gray-50 border-b border-gray-50 transition-colors group ${threadIdRef.current === session.id ? 'bg-blue-50/50' : ''}`}
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium text-gray-700 truncate group-hover:text-blue-600">
                                                                {cleanContent(session.last_message?.content) || "New conversation"}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <Clock size={10} className="text-gray-300" />
                                                                <span className="text-[10px] text-gray-400">
                                                                    {new Date(session.updated_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {threadIdRef.current === session.id && (
                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1"></div>
                                                        )}
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative flex-1 overflow-hidden flex flex-col">

                <div
                    ref={chatAreaRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar bg-gray-50/30"
                >
                    {messages.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full text-center px-4">
                            <p className="text-gray-400 text-sm">
                                {mode === 'chart' ? 'Ask me to analyze the chart patterns or trends.' : 'How can I help you find stocks today?'}
                            </p>
                        </div>
                    )}
                    {messages.map((msg, index) => (
                        <AIResponse
                            key={msg.id || index}
                            message={msg}
                            isLatest={index === messages.length - 1}
                            isLoading={isLoading}
                            streamData={streamData}
                            onApplyScreenerFilters={onApplyFilters}
                            onOpenPdf={onOpenPdf}
                            onEditProposal={handleProposalEdit}
                            onDecision={handleDecision}
                        />
                    ))}
                </div>

                {/* Scroll-to-bottom button — absolute, only when chat is visible */}
                {!isAtBottom && status === 'streaming' && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none">
                        <button
                            onClick={() => {
                                chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
                                setIsAtBottom(true);
                            }}
                            className="pointer-events-auto bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all flex items-center gap-2 px-4 py-2 mb-2 animate-in fade-in slide-in-from-bottom-2"
                        >
                            <ChevronDown size={18} />
                            <span className="text-xs font-bold">New Messages</span>
                        </button>
                    </div>
                )}

            </div>

            <div className="border-t border-gray-100 bg-white">
                <div className="p-4">
                <form onSubmit={handleSendMessage} className="relative bg-white border border-gray-200 rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                    {imagePreview && (
                        <div className="px-3 pt-3">
                            <div className="relative inline-block">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                                />
                                <button
                                    type="button"
                                    onClick={removeSelectedFile}
                                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-md hover:bg-red-600"
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="p-4 min-h-[120px] transition-all relative">
                        <div
                            ref={inputRef}
                            contentEditable={!isLoading}
                            onInput={(e) => {
                                const selection = window.getSelection();
                                if (selection.rangeCount > 0) {
                                    const range = selection.getRangeAt(0);
                                    const container = range.startContainer;

                                    if (container.nodeType === Node.TEXT_NODE) {
                                        const text = container.textContent.substring(0, range.startOffset);
                                        const words = text.split(/\s+/);
                                        const lastWord = words[words.length - 1];

                                        // Trigger on any character if symbols mode is active, or $ + 1 char
                                        if (lastWord.length >= 1) {
                                            setSuggestionQuery(lastWord);
                                            const coords = getCaretCoordinates();
                                            setCaretPosition(coords);
                                        } else {
                                            setSuggestionQuery('');
                                        }
                                    } else {
                                        setSuggestionQuery('');
                                    }
                                }
                                setLocalInput(e.target.innerText);
                            }}
                            onPaste={handlePaste}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            data-placeholder={placeholder}
                            className={`w-full min-h-[40px] focus:outline-none text-gray-700 bg-transparent text-sm empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400`}
                        />

                        {/* Suggestions Dropdown - Cursor Following */}
                        {showSuggestions && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: caretPosition.top + 38,
                                    left: caretPosition.left - 20,
                                    zIndex: 60
                                }}
                                className="bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-100 min-w-[240px] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                            >
                                <div className="px-4 py-2.5 border-b border-gray-50 bg-white">
                                    <span className="text-[11px] font-bold text-blue-600 tracking-tight">Search Results for '{suggestionQuery}'</span>
                                </div>
                                <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
                                    {suggestions.map((s) => (
                                        <button
                                            key={s.symbol}
                                            type="button"
                                            onClick={() => handleSelectSymbol(s)}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group"
                                        >
                                            <div className="flex items-center justify-center w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity">
                                                {s.symbol === 'AAPL' ? (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.96.95-2.44 1.78-3.99 1.72-1.55-.06-2.56-.83-3.61-.83-1.05 0-2.31.83-3.64.83-1.49 0-2.97-.83-4.04-1.9C.57 18.9 0 16.5 0 14.15c0-1.8.44-3.4 1.34-4.7 1.1-1.6 2.6-2.5 4.3-2.5 1.3 0 2.2.6 3.1.6.9 0 2.1-.6 3.6-.6 1.4 0 2.7.5 3.6 1.4-2.8 1.4-2.3 5.4 1 6.8-.7 1.8-1.7 3.6-2.9 5.03zM12.1 6.35c.8-.8 1.3-1.9 1.3-3.1 0-.1 0-.3-.1-.5-1.1.1-2.2.8-2.9 1.8-.7.8-1.2 2-1.2 3.1 0 .2.1.4.1.5 1.1 0 2-.6 2.8-1.8z" /></svg>
                                                ) : <Cpu size={14} className="text-gray-400 group-hover:text-blue-600" />}
                                            </div>
                                            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                                                <p className="text-sm font-semibold text-gray-800 truncate">{s.symbol}</p>
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    {s.full_symbol?.includes(':') && (
                                                        <span className="text-[10px] font-medium text-gray-400">
                                                            ({s.full_symbol.split(':')[0]})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between px-3 pb-3">
                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors flex items-center gap-2 group"
                                title="Upload Image"
                            >
                                <Paperclip size={18} className="group-hover:text-blue-600" />
                            </button>

                            <button
                                type="button"
                                onClick={() => setIsSymbolsActive(!isSymbolsActive)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${isSymbolsActive ? 'bg-blue-600 text-white shadow-md' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                            >
                                <DollarSign size={14} />
                                <span>Symbols</span>
                            </button>

                            {onActionTrigger && (
                                <button
                                    type="button"
                                    onClick={() => onActionTrigger(injectAnalysisRequest)}
                                    className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold hover:bg-blue-100 transition-colors shadow-sm"
                                >
                                    ✨ Analyze State
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
                                title="Voice Input"
                            >
                                <Mic size={18} />
                            </button>

                            <button
                                type="submit"
                                disabled={isLoading || (!(localInput || '').trim() && !selectedFile)}
                                className={`p-2 rounded-full transition-all ${((localInput || '').trim() || selectedFile) && !isLoading
                                    ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700 transform hover:scale-105'
                                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                    }`}
                                >
                                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            </button>
                        </div>
                    </div>
                </form>
                <div className="flex items-center justify-between mt-3">
                    <p className="text-[10px] text-gray-400">
                        AnalystBro AI can make mistakes. Verify important info.
                    </p>
                </div>
                </div>
            </div>
        </div>
    );
};

export default AIChatSidebar;